import React, {FC, useState, useEffect, useRef} from "react"
import moment from "moment"
import {parsePhoneNumber, formatPhoneNumberIntl} from "react-phone-number-input"

import Flex from "@irati/ui/src/Flex"
import FormattedMessage, {getMessage, useIntl} from "@irati/utils/lang"
import {Body, H3} from "@irati/ui/src/Typography"
import Button from "@irati/ui/src/Button"
import Validation from "@irati/utils/validation"
import Form from "@irati/ui/src/Form"
import Loading from "@irati/ui/src/Loading"
import {CreateConfirmIdentifierResponse} from "@irati/types"
import {getErrorMessage, getErrorValues} from "@irati/utils/lang/formatError"
import authentication from "@irati/auth"
import ErrorText from "@irati/ui/src/ErrorText"
import {Input} from "@irati/ui/src/FormItem"
import History from "@irati/utils/history"
import tracking, {LoginType} from "@irati-tracking/platform/platform"
import Alert, {AlertTypes} from "@irati/ui/src/Alert"
import {useBranding} from "@irati/brand"
import usersService from "@irati/identity/services/users"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import {BusinessLogo, PhoneImg, StyledButton} from "./styled"
import {VERIFY_PHONE_NO} from "."

let TRY_AGAIN_SECONDS: number
let MAX_CHAR_LENGTH: number

interface Props {
  phone: string
  login: CreateConfirmIdentifierResponse
  goBack: () => void
  onResend: () => void
  skipPhoneNo: () => void
}

const ConfirmPhoneCode: FC<Props> = ({login, phone, goBack, onResend, skipPhoneNo}) => {
  const [code, setCode] = useState("")
  const [load, setLoad] = useState<Promise<any> | null>(null)
  const [timer, setTimer] = useState<number>()
  const [errorMsg, setErrorMsg] = useState("")
  const [showAlert, setShowAlert] = useState<boolean>()
  const {formatMessage} = useIntl()
  const branding = useBranding()
  const inputRef = useRef<HTMLInputElement>()
  const now = moment()

  const schema = () =>
    Validation.string(formatMessage({id: "platform.defaultValidation.required"})).matches(
      /^\d{6}$/,
      formatMessage({id: "platform.defaultValidation.fixedLength"}, {count: MAX_CHAR_LENGTH})
    )

  const handleOnInputChange = (value: string) => {
    setErrorMsg("")
    setShowAlert(false)

    if (value && value.length <= MAX_CHAR_LENGTH) {
      setCode(value)
      if (value.length === MAX_CHAR_LENGTH) {
        updateUserPhoneNumber(value)
        tracking.loginOTPSubmit("otp_phone", LoginType.phoneCapture)
      }
    } else if (!value) {
      setCode("")
    }
  }

  const updateUserPhoneNumber = (input: string) => {
    const data = {
      userId: authentication.user().uuid,
      countryCode: parsePhoneNumber(phone)?.country,
      e164PhoneNo: phone,
      intlPhoneNo: formatPhoneNumberIntl(phone),
      confirmationId: login.uuid,
      identifier: phone,
      phoneCode: input
    }
    const loaded = usersService
      .updateUserVerifiedPhoneNo({data})
      .then(() => {
        tracking.loginOTPSuccess("otp_phone", LoginType.phoneCapture)
        tracking.phoneCaptureSuccess("phone_number_verification", LoginType.phoneCapture)
        localStorage.setItem(VERIFY_PHONE_NO, "{}")
        History.push({pathname: "/"})
      })
      .catch((err) => {
        const message = getMessage(formatMessage, getErrorMessage(err), getErrorValues(err))
        handleSetError(message)
        tracking.loginOTPError("otp_phone", LoginType.phoneCapture)
        tracking.loginErrorDetails(message, LoginType.phoneCapture)
      })
    setLoad(loaded)
  }

  const handleSetError = (errorMessage: string) => {
    setErrorMsg(errorMessage)
    setShowAlert(true)
  }

  const handleResendCode = () => {
    tracking.loginOTPResend("otp_phone", LoginType.phoneCapture)
    setCode("")
    setErrorMsg("")
    setShowAlert(false)
    onResend()
  }

  const setCountDown = () => {
    let countDown = TRY_AGAIN_SECONDS
    const timerId = setInterval(() => {
      if (countDown === 0) {
        clearTimeout(timerId)
      } else {
        countDown = countDown - 1
        setTimer(countDown)
      }
    }, 1000)
  }

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.maxLength = 6
    }
  }, [inputRef])

  useEffect(() => {
    if (login && login.digits && login.resend?.seconds) {
      const resend = moment.unix(parseInt(login.resend?.seconds, 10))
      const duration = moment.duration(resend.diff(now))

      TRY_AGAIN_SECONDS = Math.ceil(duration.asSeconds())
      MAX_CHAR_LENGTH = login.digits

      setTimer(TRY_AGAIN_SECONDS)
      setShowAlert(true)
      setCountDown()
    }
  }, [login])

  return (
    <>
      <Flex justifyContent="space-between" marginBottom={2}>
        <Button small tertiary onClick={goBack}>
          <Icon icon={IconTypes.angleLeft} small marginLeft={0.5} size={20} />
          <FormattedMessage id="platform.back" />
        </Button>
        <StyledButton small tertiary onClick={skipPhoneNo}>
          <FormattedMessage id="platform.oneTimePassword.enterPhone.skip" />
          <Icon icon={IconTypes.angleRight} small marginLeft={0.5} size={20} />
        </StyledButton>
      </Flex>
      <Flex direction="column" alignItems="center" justifyContent="center">
        {showAlert && (
          <Alert
            nonDismissable={errorMsg === ""}
            type={errorMsg !== "" ? AlertTypes.error : AlertTypes.success}
            title={errorMsg !== "" ? errorMsg : "platform.oneTimePassword.otpCode.alertTitle2"}
            subTitle={errorMsg !== "" ? "errors.magic.PBMagicUnexpected" : "platform.oneTimePassword.otpCode.alert2"}
            onHide={() => setShowAlert(false)}
          />
        )}
        {branding?.businessLogoUrl && <BusinessLogo src={formatMessage({id: branding?.businessLogoUrl})} />}
        <PhoneImg src={formatMessage({id: "platform.oneTimePassword.enterPhone.img"})} />
        <H3 marginBottom={0.5}>
          <FormattedMessage id="platform.oneTimePassword.enterEmail.title" />
        </H3>
        <Body small weight="light" marginBottom={2} maxWidth="100%" textAlign="center">
          <FormattedMessage
            id="platform.oneTimePassword.otpCode.subTitle"
            values={{
              digits: MAX_CHAR_LENGTH,
              email: (
                <Body small weight="semibold" display="inline">
                  {phone}
                </Body>
              ),
              back: (
                <Button small cta onClick={goBack}>
                  <FormattedMessage id="platform.oneTimePassword.otpCode.back" />
                </Button>
              )
            }}
          />
        </Body>
        <Flex direction="column" width="100%">
          <Loading load={load} />
          <Form>
            <Input
              flush
              name="otp-code"
              type="number"
              label={formatMessage({id: "platform.oneTimePassword.otpCode.inputLabel"})}
              placeholder={formatMessage({id: "platform.oneTimePassword.otpCode.inputPlaceholder"})}
              value={code}
              validation={schema()}
              onChange={handleOnInputChange}
              inputRef={(t) => (inputRef.current = t)}
            />
            {errorMsg && <ErrorText>{errorMsg}</ErrorText>}
            {timer && timer > 0 ? (
              <Body small weight="light" textAlign="center" marginBottom marginTop={2}>
                <FormattedMessage
                  id="platform.oneTimePassword.otpCode.tryAgain"
                  values={{timer: moment.utc(timer * 1000).format("mm:ss")}}
                />
              </Body>
            ) : (
              <Flex gap={1} justifyContent="center" marginTop={2} marginBottom>
                <Body small weight="light">
                  <FormattedMessage id="platform.oneTimePassword.otpCode.codeNotReceived" />
                </Body>
                <Button tertiary onClick={handleResendCode}>
                  <Flex alignItems="center">
                    <FormattedMessage id="platform.oneTimePassword.otpCode.resendBtton" />
                    <Icon icon={IconTypes.chevronRight} size={12} marginLeft={0.5} />
                  </Flex>
                </Button>
              </Flex>
            )}
          </Form>
        </Flex>
      </Flex>
    </>
  )
}

export default ConfirmPhoneCode
